/**
 * 聊天抽屉：最近几条 → 12 格固定短语 → 输入框。锚在 dockbind 上沿，和加注面板同一种抽屉。
 *
 * ★ 输入框在整个房间期间**常驻 DOM**，开合只切可见性：iOS 只认用户手势里同步发生的聚焦，
 *   而且聚焦之后元素一旦重挂，键盘会收起来。打字时抽屉的其它部分收起、只留输入行，
 *   整块翻到下注栏**下方**（talk.css 的 [data-composing]），由 `useKeyboardLift` 把下注栏抬到输入行上面：
 *   键盘 → 输入框 → 下注栏。输入框夹在中间，打字的手指碰不到弃牌。
 */
import { useEffect, useMemo, useReducer, useRef } from 'react'
import type { CSSProperties, ReactNode, RefObject } from 'react'
import { PHRASE_ROWS, PHRASE_TEXT, TALK, type CardId, type ChatEntry, type PhraseId, type PlayerView } from '@poker/shared'

import { fireHaptic } from '../fx/haptics.js'
import { IconSend } from '../icons/index.js'
import { useRoomLink } from '../room/roomLink.js'
import { playPhraseVoice, preloadSampleSfx, wantPhraseVoices } from '../fx/sampleSfx.js'
import { getAuthSession } from '../lobby/useAuth.js'
import { useSettingsStore } from '../store/index.js'
import { toCardIdsSafe } from '../room/buildTableVM.js'
import { rankText, suitKey, suitSymbol } from '../table/cardFormat.js'
import { selectNetView, useNetStore } from '../transport/applyMessage.js'
import {
  handLive,
  latestAt,
  phraseCooldownMs,
  talkLines,
  textCooldownMs,
} from './talkDerive.js'
import { useTalkStore, type TalkState } from './talkStore.js'
import { noteTyping } from './typingGuard.js'
import { useKeyboardLift } from './useKeyboardLift.js'

/** 抽屉里最多回看多少条（客户端本来也只留 30 条） */
const LOG_LIMIT = 30

export interface TalkDrawerProps {
  /** dockbind：键盘抬升量按它的底边算 */
  readonly anchorRef: RefObject<HTMLElement | null>
  /**
   * 覆盖 store 里的值（回放 / storybook / 单测用；省略就订阅 store）。
   * 与 WaitingRoom.view、ActionDockBinding.prompt 同一个约定。
   */
  readonly view?: PlayerView | null
  readonly chat?: readonly ChatEntry[]
  readonly online?: boolean
  readonly talk?: Partial<Pick<TalkState, 'open' | 'composing' | 'draft' | 'notice' | 'lastTextAt' | 'phraseStamps'>>
  readonly bubbles?: boolean
}

function MiniHole({ cards }: { readonly cards: readonly CardId[] }): ReactNode {
  return (
    <span className="talk__hole" aria-label="你的底牌">
      {cards.map((c) => (
        // 花色颜色跟全站同一套变量（四色 / 双色牌由 html[data-deck] 切换）
        <span key={c} className="talk__card" data-suit={suitKey(c)}>
          {rankText(c)}
          <i>{suitSymbol(c)}</i>
        </span>
      ))}
    </span>
  )
}

export function TalkDrawer({ anchorRef, ...over }: TalkDrawerProps): ReactNode {
  const link = useRoomLink()
  const storeView = useNetStore(selectNetView)
  const storeChat = useNetStore((s) => s.chat)
  const storeOnline = useNetStore((s) => s.phase === 'live')
  const storeTalk = useTalkStore()
  const storeBubbles = useSettingsStore((s) => s.chatBubbles)
  const view = over.view === undefined ? storeView : over.view
  const chat = over.chat ?? storeChat
  const online = over.online ?? storeOnline
  const open = over.talk?.open ?? storeTalk.open
  const composing = over.talk?.composing ?? storeTalk.composing
  const draft = over.talk?.draft ?? storeTalk.draft
  const notice = over.talk?.notice === undefined ? storeTalk.notice : over.talk.notice
  const lastTextAt = over.talk?.lastTextAt === undefined ? storeTalk.lastTextAt : over.talk.lastTextAt
  const phraseStamps = over.talk?.phraseStamps ?? storeTalk.phraseStamps
  const t = useTalkStore.getState()
  const bubbles = over.bubbles ?? storeBubbles
  const haptics = useSettingsStore((s) => s.haptics)
  const setSetting = useSettingsStore((s) => s.set)
  const [, tick] = useReducer((x: number) => x + 1, 0)

  const inputRef = useRef<HTMLInputElement>(null)
  const sectionRef = useRef<HTMLElement>(null)
  const logRef = useRef<HTMLOListElement>(null)

  // 量整块抽屉而不是输入行：打字态下抽屉只剩输入行，但它自己的上下内边距与上边框也要落在键盘之上
  useKeyboardLift(composing, anchorRef, sectionRef)

  const me = view?.viewer.accountId ?? null
  const lines = talkLines(chat, me, LOG_LIMIT)
  const nowLocal = Date.now()
  const phraseWait = phraseCooldownMs(phraseStamps, nowLocal)
  const textWait = textCooldownMs(lastTextAt, nowLocal)
  // 冷却条的时长只在「又发了一条」时算一次：每次重渲染都重写 --cool-ms 会改掉正在跑的
  // CSS 动画的时长，进度条跳一下、提前走完
  const coolMs = useMemo(() => phraseCooldownMs(phraseStamps, Date.now()), [phraseStamps])

  // 冷却结束时重画一次（只挂一个定时器，不逐帧）
  useEffect(() => {
    const wait = Math.max(phraseWait, textWait)
    if (wait <= 0) return undefined
    const id = setTimeout(tick, wait + 20)
    return () => clearTimeout(id)
  }, [phraseWait, textWait])

  // 开着的时候新来的话都算看过；滚到最新
  const newest = latestAt(chat)
  useEffect(() => {
    if (!open) return
    useTalkStore.getState().markSeen(newest)
    const log = logRef.current
    if (log !== null) log.scrollTop = log.scrollHeight
  }, [open, newest])

  // 这张桌子开着聊天：之后的手势连同短语语音一起预取（本地模式和关了聊天的房间不下载）
  useEffect(() => {
    wantPhraseVoices()
  }, [])

  // ★ 卸载时（离开房间、房主关掉聊天）把打字 / 打开态清掉：输入框带着焦点被卸载时
  //   React 不会派发 onBlur，composing 会停在 true —— 下次挂载时 useKeyboardLift 又把壳层冻上，
  //   抽屉也会按旧的 open 先弹出来再收回
  useEffect(
    () => () => {
      const s = useTalkStore.getState()
      s.setComposing(false)
      s.setOpen(false)
    },
    [],
  )

  // 抽屉被收起（点遮罩 / 发完话 / 换房间）时输入框必须一起失焦，否则键盘挂在空气里
  useEffect(() => {
    const el = inputRef.current
    if (!open && el !== null && globalThis.document?.activeElement === el) el.blur()
  }, [open])

  const ownedSeat = view?.viewer.ownedSeat ?? null
  const hole = ownedSeat === null ? null : (view?.seats[ownedSeat]?.holeCards ?? null)
  const holeCards =
    hole !== null && (hole.visibility === 'mine' || hole.visibility === 'shown') ? toCardIdsSafe(hole.cards) : null

  const close = (): void => {
    inputRef.current?.blur()
    t.setOpen(false)
  }

  const sendPhrase = (id: PhraseId, target: HTMLElement): void => {
    if (!online) return
    const at = Date.now()
    if (phraseCooldownMs(useTalkStore.getState().phraseStamps, at) > 0) return
    const cid = link.send({ type: 'phrase', phrase: id })
    useTalkStore.getState().notePhrase(cid, at)
    fireHaptic('select', haptics, target)
    // 点了就响：这一下是用户手势，iOS 上也放得出声。服务器回显时不再重复播（useTalkFeed 跳过自己的）
    const sound = useSettingsStore.getState().sound
    playPhraseVoice(id, getAuthSession().account()?.voice, sound, { fromGesture: true })
    // 顺手把别人短语的录音预取下来（这一下 click 一定算用户激活，解锁已经在上面那句里做了）。
    // 音效关着就不碰音频：不建 AudioContext、不下载
    if (sound) preloadSampleSfx()
    // 手牌进行中说完就把牌桌还给你；等待室里不收，方便连着斗嘴
    if (handLive(useNetStore.getState().view)) close()
  }

  const sendText = (): void => {
    const s = useTalkStore.getState()
    const text = s.draft.trim()
    if (text === '' || !online) return
    const at = Date.now()
    if (textCooldownMs(s.lastTextAt, at) > 0) {
      s.setNotice('说太快了，等一下')
      return
    }
    const cid = link.send({ type: 'chat', text })
    s.noteText(cid, text, at)
    s.setDraft('')
    if (handLive(useNetStore.getState().view)) close()
  }

  const coolStyle = { '--cool-ms': `${String(coolMs)}ms` } as CSSProperties

  return (
    <>
      <div
        className="talk-scrim"
        data-open={open ? 'on' : 'off'}
        aria-hidden="true"
        onPointerDown={(e) => {
          e.preventDefault()
          close()
        }}
      />
      <section
        ref={sectionRef}
        id="talk-drawer"
        className="talk"
        data-open={open ? 'on' : 'off'}
        data-composing={composing ? 'on' : 'off'}
        aria-label="牌桌聊天"
        aria-hidden={!open}
      >
        <div className="talk__grip" aria-hidden="true" />
        <div className="talk__cool" aria-hidden="true">
          {phraseWait > 0 && <i key={phraseStamps.at(-1) ?? 0} style={coolStyle} />}
        </div>
        <div className="talk__head">
          <span>最近</span>
          <label className="talk__switch">
            <input
              id="talk-bubbles"
              type="checkbox"
              checked={bubbles}
              onChange={(e) => setSetting('chatBubbles', e.target.checked)}
            />
            <span>牌桌气泡</span>
          </label>
        </div>
        <ol className="talk__log" ref={logRef} role="log" aria-live="polite">
          {lines.length === 0 && <li className="talk__empty">还没人说话</li>}
          {lines.map((l) => (
            <li key={l.id} data-mine={l.mine ? 'on' : 'off'} data-system={l.system ? 'on' : 'off'}>
              {!l.system && <span className="talk__who">{l.who}</span>}
              <span className={l.phrase ? 'talk__text talk__text--phrase' : 'talk__text'}>{l.text}</span>
            </li>
          ))}
        </ol>
        <div className="talk__grid" data-cool={phraseWait > 0 ? 'on' : 'off'}>
          {PHRASE_ROWS.map((row) => (
            <div key={row.label} className="talk__row" role="group" aria-label={row.label}>
              {row.ids.map((id) => (
                <button
                  key={id}
                  type="button"
                  className="talk__phrase pressable"
                  disabled={!online || phraseWait > 0}
                  onClick={(e) => sendPhrase(id, e.currentTarget)}
                >
                  {PHRASE_TEXT[id]}
                </button>
              ))}
            </div>
          ))}
        </div>
        {(notice !== null || !online) && (
          <p className="talk__notice" role="status">
            {online ? notice : '网络断开，暂时发不出去'}
          </p>
        )}
        <div className="talk__inrow">
          {composing && holeCards !== null && <MiniHole cards={holeCards} />}
          <input
            ref={inputRef}
            id="talk-input"
            className="talk__input"
            type="text"
            enterKeyHint="send"
            maxLength={TALK.CHAT_INPUT_MAX}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            placeholder={online ? '说点什么…' : '网络断开'}
            aria-label="输入聊天内容"
            value={draft}
            disabled={!online}
            onFocus={() => t.setComposing(true)}
            onBlur={() => t.setComposing(false)}
            onChange={(e) => {
              noteTyping()
              t.setDraft(e.target.value)
            }}
            onKeyDown={(e) => {
              noteTyping()
              // 拼音输入法按回车是「上屏」不是「发送」：组字中（isComposing / 229）一律放过
              if (e.key !== 'Enter' || e.nativeEvent.isComposing || e.keyCode === 229) return
              e.preventDefault()
              sendText()
            }}
          />
          <button
            type="button"
            className="talk__send pressable"
            aria-label="发送"
            disabled={!online || draft.trim() === '' || textWait > 0}
            // 按下时不让输入框失焦：iOS 上失焦会先收键盘，点击就落空了
            onPointerDown={(e) => e.preventDefault()}
            onClick={sendText}
          >
            <IconSend size={18} />
          </button>
        </div>
      </section>
    </>
  )
}
